if (!customElements.get("product-form")) {
	customElements.define(
		"product-form",
		class ProductForm extends HTMLElement {
			constructor() {
				super();

				this.form = this.querySelector("form");
				if (!this.form) return;

				this.cart = document.querySelector("cart-drawer");
				this.submitButton = this.querySelector('[type="submit"]');
				this.hideErrors = this.dataset.hideErrors === "true";
				this.variantPicker = this.getVariantPicker();

				this.enableVariantInputs();
				this.syncVariantId();

				this.form.addEventListener(
					"submit",
					this.onSubmitHandler.bind(this)
				);

				if (this.variantPicker) {
					this.variantPicker.addEventListener("change", () => {
						window.requestAnimationFrame(() => this.syncVariantId());
					});
				}

				if (this.cart && this.submitButton) {
					this.submitButton.setAttribute("aria-haspopup", "dialog");
				}
			}

			getVariantIdInputs() {
				return Array.from(this.form.querySelectorAll('[name="id"]'));
			}

			enableVariantInputs() {
				this.getVariantIdInputs().forEach((input) => {
					input.disabled = false;
				});
			}

			getProductScope() {
				return (
					this.closest(".product-section") ||
					this.closest("quick-add-modal") ||
					this.closest(".shopify-section") ||
					document
				);
			}

			getVariantPicker() {
				const scope = this.getProductScope();
				const formId = this.form.id;

				if (formId) {
					const pickers = scope.querySelectorAll(
						"variant-radios[data-product-form-id], variant-selects[data-product-form-id]"
					);
					const matchingPicker = Array.from(pickers).find(
						(picker) => picker.dataset.productFormId === formId
					);

					if (matchingPicker) return matchingPicker;
				}

				return scope.querySelector("variant-radios, variant-selects");
			}

			parseVariantData(selector) {
				const source = this.variantPicker?.querySelector(selector);
				if (!source?.textContent) return null;

				try {
					return JSON.parse(source.textContent);
				} catch (error) {
					console.error("Variant data could not be parsed.", error);
					return null;
				}
			}

			getSelectedOptions() {
				if (!this.variantPicker) return [];

				return Array.from(
					this.variantPicker.querySelectorAll(".product-form__controls")
				)
					.map((control) => {
						const checkedRadio = control.querySelector(
							'input[type="radio"]:checked'
						);
						if (checkedRadio) return checkedRadio.value;

						const hiddenOption = control.querySelector(
							'variant-dropdown-select input[type="hidden"]'
						);
						if (hiddenOption) return hiddenOption.value;

						const select = control.querySelector("select");
						return select?.value || null;
					})
					.filter((value) => value !== null && value !== "");
			}

			getVariantFromSelectedOptions() {
				const variants = this.parseVariantData("[data-all-variants-no-high]");
				const selectedOptions = this.getSelectedOptions();

				if (Array.isArray(variants) && selectedOptions.length > 0) {
					const matchingVariant = variants.find((variant) => {
						if (!Array.isArray(variant.options)) return false;
						return variant.options.every(
							(value, index) => value === selectedOptions[index]
						);
					});

					if (matchingVariant) return matchingVariant;
				}

				return null;
			}

			syncVariantId() {
				this.enableVariantInputs();

				const variantInputs = this.getVariantIdInputs();
				const existingValue = variantInputs.find((input) => input.value)?.value;
				const matchedVariant = this.getVariantFromSelectedOptions();
				const initialVariant = this.parseVariantData("[data-selected-variant]");
				const variantId =
					matchedVariant?.id || existingValue || initialVariant?.id || "";

				if (!variantId) return "";

				variantInputs.forEach((input) => {
					input.value = String(variantId);
					input.disabled = false;
				});

				return String(variantId);
			}

			getLoadingSpinner() {
				return (
					this.submitButton?.querySelector(".loading-overlay__spinner") ||
					this.querySelector(".loading-overlay__spinner")
				);
			}

			onSubmitHandler(event) {
				event.preventDefault();
				if (!this.submitButton || !this.form) return;

				if (
					this.submitButton.getAttribute("aria-disabled") === "true" ||
					this.submitButton.disabled
				) {
					return;
				}

				const variantId = this.syncVariantId();
				if (!variantId) {
					this.handleErrorMessage(
						"Bitte wählen Sie eine verfügbare Produktvariante aus."
					);
					return;
				}

				this.handleErrorMessage();
				this.submitButton.setAttribute("aria-disabled", "true");
				this.submitButton.classList.add("loading");
				this.getLoadingSpinner()?.classList.remove("hidden");

				const config = fetchConfig("javascript");
				config.headers["X-Requested-With"] = "XMLHttpRequest";
				delete config.headers["Content-Type"];

				const formData = new FormData(this.form);
				formData.set("id", variantId);

				if (this.cart) {
					formData.set(
						"sections",
						this.cart
							.getSectionsToRender()
							.map((section) => section.id)
							.join(",")
					);
					formData.set("sections_url", window.location.pathname);
					this.cart.setActiveElement(document.activeElement);
				}

				config.body = formData;
				let itemWasAdded = false;

				fetch(`${routes.cart_add_url}`, config)
					.then((response) => response.json())
					.then((response) => {
						if (response.status) {
							this.error = true;

							publish(PUB_SUB_EVENTS.cartError, {
								source: "product-form",
								productVariantId: variantId,
								errors: response.description,
								message: response.message,
							});

							this.handleErrorMessage(
								response.description || response.message
							);

							const soldOutMessage =
								this.submitButton.querySelector(".sold-out-message");
							const soldOutLabel = this.submitButton.querySelector(
								".card__quickview-text, .button__label, span"
							);

							soldOutLabel?.classList.add("hidden");
							soldOutMessage?.classList.remove("hidden");
							return;
						}

						itemWasAdded = true;
						this.error = false;

						if (!this.cart) {
							window.location = window.routes.cart_url;
							return;
						}

						publish(PUB_SUB_EVENTS.cartUpdate, {
							source: "product-form",
							productVariantId: variantId,
						});

						const quickAddModal = this.closest("quick-add-modal");
						if (quickAddModal) {
							document.body.addEventListener(
								"modalClosed",
								() => {
									window.setTimeout(() => {
										this.cart.renderContents(response);
									});
								},
								{ once: true }
							);
							quickAddModal.hide(true);
						} else {
							this.cart.renderContents(response);
						}
					})
					.catch((error) => {
						console.error(error);
						this.error = true;
						this.handleErrorMessage(
							"Der Artikel konnte nicht hinzugefügt werden. Bitte versuchen Sie es erneut."
						);
					})
					.finally(() => {
						if (!this.submitButton) return;

						this.submitButton.classList.remove("loading");
						this.getLoadingSpinner()?.classList.add("hidden");

						if (itemWasAdded && this.cart?.classList.contains("is-empty")) {
							this.cart.classList.remove("is-empty");
						}

						if (!this.error) {
							this.submitButton.removeAttribute("aria-disabled");
						}
					});
			}

			handleErrorMessage(errorMessage = false) {
				if (this.hideErrors) return;

				this.errorMessageWrapper =
					this.errorMessageWrapper ||
					this.querySelector(".product-form__error-message-wrapper");

				if (!this.errorMessageWrapper) return;

				this.errorMessage =
					this.errorMessage ||
					this.errorMessageWrapper.querySelector(
						".product-form__error-message"
					);

				this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

				if (errorMessage && this.errorMessage) {
					this.errorMessage.textContent = errorMessage;
				}
			}
		}
	);
}
